<template>
  <v-card>
    <v-card-title class="success">
      <h3 class="headline white--text">Free Chlorine (FC)</h3>
    </v-card-title>
    <v-card-text>
      <h4 class="subheading">
        <strong>GOAL RANGE:</strong>
        {{ settings.range.fc.low }} to {{ settings.range.fc.high }}
      </h4>
      <v-form>
        <v-layout row wrap>
          <v-flex class="pl-1 xs6">
            <v-text-field label="Tested" v-model="fc.tested" @input="update" />
          </v-flex>
          <v-flex class="pl-1 xs6">
            <v-text-field label="Goal" v-model="fc.goal" @input="update" />
          </v-flex>
          <v-flex class="pl-1 xs12">
            <v-select
              label="Type"
              v-model="fc.type"
              :items="fc.types"
              item-text="name"
              :return-object="true"
              @input="update"
            />
          </v-flex>
          <v-flex class="pl-1 xs12 md6" v-if="fc.type.name === 'bleach'">
            <v-text-field
              label="Percent (%)"
              v-model="fc.percent"
              @input="update"
            />
          </v-flex>
          <v-flex class="pl-1 xs12 md6" v-if="fc.type.name === 'bleach'">
            <v-select
              label="Jug Size"
              v-model="fc.jugSize"
              :items="fc.jugSizes"
              item-text="name"
              :return-object="true"
              @input="update"
            />
          </v-flex>
        </v-layout>
        <a
          target="_blank"
          href="https://www.poolcalculator.com/pool-chemistry/free-chlorine-vs-total-chlorine/"
          >Learn more about Free Chlorine »</a
        >
        <h4 class="subheading mt-3" v-if="fc.tested != fc.goal">
          <strong>SUGGESTION:</strong>
        </h4>
        <p v-if="fc.tested > fc.goal" class="mt-3">
          No action needed. It is okay for tested Free Chlorine to be higher
          than goal Free Chlorine.
        </p>
        <div v-else-if="fc.tested < fc.goal" class="mt-2">
          <p v-if="fc.oz > 0 && fc.vol > 0">
            Add {{ fc.oz }} {{ units.weight }} by weight or {{ fc.vol }}
            {{ units.volume }} by volume of {{ fc.type.name }}.
          </p>
          <p v-else-if="fc.oz > 0">
            Add {{ fc.oz }} {{ units.weight }} by weight of {{ fc.type.name }}.
          </p>
          <p v-else-if="fc.vol > 0">
            Add {{ fc.vol }} {{ units.volume }} by volume of {{ fc.type.name }}.
          </p>
          <p v-if="fc.type.name === 'bleach'">
            {{ fc.vol }} {{ units.volume }} is
            {{ util.printBleach(fc.vol, fc.jugSize.value, settings.unitType) }}.
          </p>
          <p class="mt-3" v-else>
            <span v-if="fc.oz > 0">
              {{ fc.oz }} {{ units.weight }} is
              {{ util.printWeight(fc.oz, settings.unitType) }}.
              <br />
            </span>
            <span v-if="fc.vol > 0"
              >{{ fc.vol }} {{ units.volume }} is
              {{ util.printVolume(fc.vol, settings.unitType) }}.</span
            >
          </p>
          <p>
            <strong>NOTE:</strong> Dichlor and trichlor add CYA and lower pH.
            Cal-hypo adds CH.
          </p>
          <PC-Dialog
            v-if="firebase.auth.currentUser"
            type="Log FC Addition"
            :fields="logFields"
            :showDialog="showDialog"
            @close="close"
            @save="save"
          >
            <template v-slot:button>
              <v-btn block color="success" class="white--text mt-3" @click="log"
                >Log Addition</v-btn
              >
            </template>
          </PC-Dialog>
        </div>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import PCDialog from "@/components/PCDialog";
import firebase from "@/firebase";
import util from "@/util";
export default {
  name: "FC",
  components: { PCDialog },
  data() {
    return {
      firebase,
      util,
      logFields: [
        {
          name: "Type",
          type: "select",
          value: "",
          items: [],
          classes: ["mt-2", "pl-1", "xs12"],
        },
        {
          name: "Amount",
          type: "text",
          value: "",
          classes: ["pl-1", "xs12", "md6"],
        },
        {
          name: "Units",
          type: "select",
          value: "",
          items: ["oz", "lbs", "fl oz", "gal", "g", "kg", "ml", "l"],
          classes: ["pl-1", "xs12", "md6"],
        },
      ],
      showDialog: false,
    };
  },
  computed: {
    ...mapGetters({
      fc: "fc/getFC",
      settings: "settings/getSettings",
    }),
    units() {
      return this.util.getUnits(this.settings.unitType);
    },
  },
  methods: {
    ...mapActions({
      initFC: "fc/initFC",
      saveFC: "fc/saveFC",
    }),
    ...mapMutations({
      calcFC: "fc/calcFC",
      setFC: "fc/setFC",
    }),
    close() {
      this.showDialog = false;
    },
    log() {
      if (this.fc.type.name === "bleach") {
        this.logFields[0].type = "text";
        this.logFields[0].value = `${this.fc.percent}% bleach`;
      } else {
        this.logFields[0].type = "select";
      }
      this.showDialog = true;
    },
    async save() {
      await firebase.db
        .collection("users")
        .doc(firebase.auth.currentUser.uid)
        .collection("logs")
        .add({
          pool: this.settings.pool.name,
          timestamp: new Date().toISOString(),
          chemical: this.logFields[0].value.name || this.logFields[0].value,
          amount: Number(this.logFields[1].value),
          units: this.logFields[2].value,
        });
      this.showDialog = false;
    },
    async update() {
      this.validateRange();
      this.setFC(this.fc);
      this.calcFC(this.settings);
      await this.saveFC(this.fc);
    },
    validateRange() {
      if (this.fc.tested < 0) this.fc.tested = 0;
      if (this.fc.tested > 25) this.fc.tested = 25;
      if (this.fc.goal < 0) this.fc.goal = 0;
      if (this.fc.goal > 25) this.fc.goal = 25;
    },
  },
  async mounted() {
    await this.initFC();
    this.logFields[0].value = this.fc.type;
    this.logFields[0].items = this.fc.types;
  },
};
</script>
